import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { GlobalStyle as StashUIGlobalStyle, ThemeProvider } from '@stashinvest/ui';
import ErrorBoundary from 'stash/components/screens/error-boundary/ErrorBoundary';
import { createStore } from 'stash/store';
import Routes from './Routes';
import GlobalStyle from './GlobalStyle';
import DevTools from 'stash/components/screens/dev/DevTools';
import { deleteCookie, isNativeWebview, isNotProd, setUTMCookies } from 'stash/utils';
import Dialog from 'stash/components/global/Dialog';
import { SEGMENT_CDN_PROXY_URL, SEGMENT_WRITE_KEY } from 'stash/constants';
import { SegmentAnalyticsProvider } from './stash/hooks/useAnalytics';
import { datadogRum } from '@datadog/browser-rum';
import datadogRumConfig from 'stash/utils/datadogRum';
import { BanjoProvider } from '@stashinvest/react-banjo';
import { languageMap } from 'banjo';
import {
	ExperimentsContextUpdater,
	getLaunchDarklyProvider,
	resolveDeviceId,
} from 'stash/components/experiments';
import './App.css';
import { NativeAuthProvider } from 'stash/components/native-auth-provider/NativeAuthProvider';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { createBrowserHistory } from 'history';
window.Stash.version = import.meta.env.VITE_APP_VERSION;

// TODO: incident mitigation remove from next release
deleteCookie('stashBankLinkCardDetails');

datadogRum.init(datadogRumConfig);

export const ReactApp = ({ analytics }) => {
	const history = createBrowserHistory();
	const store = createStore({ analytics });

	if (isNativeWebview()) {
		datadogRum.startSessionReplayRecording();
	}

	return (
		<Provider store={store}>
			<ThemeProvider>
				<BanjoProvider defaultLocale="en-us" languageMap={languageMap} variants={[]}>
					<StashUIGlobalStyle />
					<GlobalStyle />
					<NativeAuthProvider>
						<ExperimentsContextUpdater />
						<ErrorBoundary>
							{isNotProd() && <DevTools />}
							<SegmentAnalyticsProvider analytics={analytics}>
								<Routes history={history} />
							</SegmentAnalyticsProvider>
							<ThemeProvider mode="light">
								<Dialog />
							</ThemeProvider>
						</ErrorBoundary>
					</NativeAuthProvider>
				</BanjoProvider>
			</ThemeProvider>
		</Provider>
	);
};

/**
 * Re-assert the boot-resolved device id on Segment so SDK tracks match LD and
 * auth handoff (maTrackEvent, busting URLs). Idempotent when already equal.
 * Always watch for slow/blocked Segment and emit the Datadog timeout signal.
 */
function reportSegmentBootstrapError(error, { timeoutMs, errorType }) {
	datadogRum.addError(error, {
		source: 'segment-bootstrap',
		component: 'index.jsx',
		timeoutMs,
		errorType,
	});
}

async function safeSetAnonymousId(client, deviceId, timeoutMs) {
	try {
		if (client && typeof client.setAnonymousId === 'function') {
			// await so sync throws and buffered promise rejections both hit catch
			await client.setAnonymousId(deviceId);
		}
	} catch (error) {
		reportSegmentBootstrapError(error, {
			timeoutMs,
			errorType: 'set-anonymous-id-failure',
		});
	}
}

async function alignSegmentAnonymousId(analyticsBrowser, deviceId) {
	const SEGMENT_LOAD_TIMEOUT_MS = 4000;

	// Pre-ready AnalyticsBrowser.setAnonymousId returns a buffered promise;
	// do not await: LD / paint must not wait on Segment.
	void safeSetAnonymousId(analyticsBrowser, deviceId, SEGMENT_LOAD_TIMEOUT_MS);

	let loadTimeoutId;
	try {
		const timedOut = new Promise((_, reject) => {
			loadTimeoutId = setTimeout(
				() => reject(new Error('Segment load timeout')),
				SEGMENT_LOAD_TIMEOUT_MS
			);
		});

		const ready = await Promise.race([Promise.resolve(analyticsBrowser), timedOut]);
		clearTimeout(loadTimeoutId);

		const client = Array.isArray(ready) ? ready[0] : ready;

		// Re-assert after ready in case the SDK minted before the queued call applied.
		await safeSetAnonymousId(client, deviceId, SEGMENT_LOAD_TIMEOUT_MS);

		return client;
	} catch (error) {
		clearTimeout(loadTimeoutId);
		reportSegmentBootstrapError(error, {
			timeoutMs: SEGMENT_LOAD_TIMEOUT_MS,
			errorType: error?.message === 'Segment load timeout' ? 'timeout' : 'load-failure',
		});
		return undefined;
	}
}

(async () => {
	setUTMCookies();

	// Own the device id synchronously before LD boot so bucketing never depends
	// on Segment CDN timing.
	const { id: deviceId, source: deviceIdSource } = resolveDeviceId();
	datadogRum.addAction('ld_device_id_resolved', { source: deviceIdSource });

	const analytics = AnalyticsBrowser.load({
		writeKey: SEGMENT_WRITE_KEY,
		cdnURL: SEGMENT_CDN_PROXY_URL,
	});

	// Queue identity align before any component can track. Do not gate LD or paint.
	void alignSegmentAnonymousId(analytics, deviceId);
	if (deviceIdSource === 'query' && typeof analytics.identify === 'function') {
		// Preserve prior BustingWebView handoff hygiene (was in OnboardingApp).
		analytics.identify({ context: { stash_clean: true } });
	}

	const LaunchDarklyProvider = await getLaunchDarklyProvider(deviceId);

	const container = document.getElementById('root');
	const root = createRoot(container, {
		// React 19 replaces default reportError/console.error when these are set.
		// Report once via addError (with React context) — do not also console.error /
		// reportError or Datadog RUM would double-count (it tracks console/global errors).
		onUncaughtError: (error, errorInfo) => {
			datadogRum.addError(error, {
				source: 'react-root',
				errorType: 'uncaught',
				componentStack: errorInfo?.componentStack,
			});
		},
		onCaughtError: (error, errorInfo) => {
			datadogRum.addError(error, {
				source: 'react-root',
				errorType: 'caught',
				componentStack: errorInfo?.componentStack,
			});
		},
		onRecoverableError: (error, errorInfo) => {
			datadogRum.addError(error, {
				source: 'react-root',
				errorType: 'recoverable',
				componentStack: errorInfo?.componentStack,
			});
		},
	});
	root.render(
		<LaunchDarklyProvider>
			<ReactApp analytics={analytics} />
		</LaunchDarklyProvider>
	);
})();
